import i18next from 'i18next';
import { toast } from 'sonner';

import { useDeadLetterStore } from '@/store/dead-letter.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

const t = (k, fb) => i18next.t(k, { defaultValue: fb });
const errMsg = (e, fb) => getIntegrationErrorMessage(e) || fb;

export class DeadLetterIntegration {
    constructor(service) {
        this.service = service;
    }

    async fetchMetrics() {
        const s = useDeadLetterStore.getState();
        s.setMetricsLoading(true);
        try {
            const r = await this.service.metrics();
            s.setMetrics(r.data || null);
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, t('admin:deadLetterQueue.loadFailed', { defaultValue: 'Could not load metrics' }));
            toast.error(m);
            return { success: false, error: m };
        } finally {
            s.setMetricsLoading(false);
        }
    }

    async fetchList(params = {}) {
        const s = useDeadLetterStore.getState();
        const q = { ...s.searchParams, ...params };
        s.setSearchParams(q);
        s.setLoading(true);
        try {
            const r = await this.service.list(q);
            const data = r.data ?? [];
            const pag = r.pagination || {};
            s.setItems(Array.isArray(data) ? data : []);
            s.setPaginationData({
                page: pag.page || q.page || 1,
                pageSize: pag.pageSize || q.pageSize || 20,
                total: pag.total || 0,
                totalPages: pag.totalPages || 1,
            });
            return { success: true, data };
        } catch (e) {
            const m = errMsg(e, t('admin:deadLetterQueue.loadFailed', { defaultValue: 'Could not load list' }));
            toast.error(m);
            return { success: false, error: m };
        } finally {
            s.setLoading(false);
        }
    }

    async fetchById(id) {
        try {
            const r = await this.service.getById(id);
            useDeadLetterStore.getState().setSelected(r.data || null);
            return { success: true, data: r.data };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:couldNotLoad', { defaultValue: 'Could not load' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async retry(id, { silent = false } = {}) {
        try {
            const r = await this.service.retry(id);
            if (!silent) {
                toast.success(t('admin:deadLetterQueue.messages.retrySuccess', { defaultValue: 'Resent' }));
            }
            await this.fetchList();
            await this.fetchMetrics();
            return { success: true };
        } catch (e) {
            if (!silent) {
                const m = errMsg(e, t('admin:deadLetterQueue.retryFailed', { defaultValue: 'Could not resend' }));
                toast.error(m);
                return { success: false, error: m };
            }
            throw e;
        }
    }

    async abandon(id, { silent = false } = {}) {
        try {
            const r = await this.service.abandon(id);
            if (!silent) {
                toast.success(t('admin:deadLetterQueue.messages.abandonSuccess', { defaultValue: 'Cancelled' }));
            }
            await this.fetchList();
            await this.fetchMetrics();
            return { success: true };
        } catch (e) {
            if (!silent) {
                const m = errMsg(e, t('admin:deadLetterQueue.abandonFailed', { defaultValue: 'Could not cancel' }));
                toast.error(m);
                return { success: false, error: m };
            }
            throw e;
        }
    }

    async retryMany(ids) {
        let ok = 0;
        let fail = 0;
        for (const id of ids) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await this.service.retry(id);
                ok += 1;
            } catch {
                fail += 1;
            }
        }
        toast.success(
            t('admin:deadLetterQueue.messages.bulkRetrySelected', {
                defaultValue: '{{ok}} records resent, {{fail}} failed.',
                ok,
                fail,
            }),
        );
        await this.fetchList();
        await this.fetchMetrics();
        return { success: true, ok, fail };
    }

    async abandonMany(ids) {
        let ok = 0;
        let fail = 0;
        for (const id of ids) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await this.service.abandon(id);
                ok += 1;
            } catch {
                fail += 1;
            }
        }
        toast.success(
            t('admin:deadLetterQueue.messages.bulkAbandonSelected', {
                defaultValue: '{{ok}} records cancelled, {{fail}} failed.',
                ok,
                fail,
            }),
        );
        await this.fetchList();
        await this.fetchMetrics();
        return { success: true, ok, fail };
    }

    async retryAll(body = {}) {
        try {
            const r = await this.service.retryAll(body);
            toast.success(t('admin:deadLetterQueue.messages.bulkRetrySuccess', { defaultValue: 'Bulk resend completed' }));
            await this.fetchList();
            await this.fetchMetrics();
            return { success: true };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:bulkOperationFailed', { defaultValue: 'Bulk operation failed' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async abandonAll(body = {}) {
        try {
            const r = await this.service.abandonAll(body);
            toast.success(t('admin:deadLetterQueue.messages.bulkAbandonSuccess', { defaultValue: 'Bulk cancel completed' }));
            await this.fetchList();
            await this.fetchMetrics();
            return { success: true };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:bulkOperationFailed', { defaultValue: 'Bulk operation failed' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }

    async purge(body = {}) {
        try {
            const r = await this.service.purge(body);
            toast.success(t('admin:deadLetterQueue.messages.purgeSuccess', { defaultValue: 'Cleanup completed' }));
            await this.fetchMetrics();
            return { success: true };
        } catch (e) {
            const m = errMsg(e, i18next.t('integrationToasts:cleanupFailed', { defaultValue: 'Cleanup failed' }));
            toast.error(m);
            return { success: false, error: m };
        }
    }
}
